import React from 'react';
import ProductCard from './ProductCard';
import ProductNewRibbon from './ProductNewRibbon';
import PriceWithVatLabel from './PriceWithVatLabel';
import { Product } from '../types';
import {
  LJUS_TIER_STICKY_NAV,
  groupLjusProducts,
  type LjusTierId,
} from '../config/ljusProductTiers';
import { trackBookingCtaClick } from '../utils/siteAnalytics';

type ViewMode = 'grid' | 'list';

type Props = {
  products: Product[];
  viewMode: ViewMode;
};

function tierSectionClass(tier: LjusTierId): string {
  switch (tier) {
    case 'festljus':
      return 'border-purple-500/40 bg-gradient-to-br from-purple-950/40 via-slate-900/35 to-slate-900/20';
    case 'stamningsljus':
      return 'border-slate-700 bg-slate-900/35';
    case 'scen_effektljus':
      return 'border-slate-600/80 bg-slate-950/55';
    default:
      return 'border-slate-800 bg-slate-900/30';
  }
}

function tierTitleClass(tier: LjusTierId): string {
  switch (tier) {
    case 'festljus':
      return 'text-purple-200';
    case 'scen_effektljus':
      return 'text-white';
    default:
      return 'text-slate-100';
  }
}

function tierAccentBar(tier: LjusTierId): string {
  switch (tier) {
    case 'festljus':
      return 'bg-purple-500';
    case 'stamningsljus':
      return 'bg-violet-400/80';
    case 'scen_effektljus':
      return 'bg-slate-400';
    default:
      return 'bg-slate-600';
  }
}

const ProductListRow: React.FC<{ product: Product }> = ({ product }) => (
  <div className="bg-slate-900/35 border border-slate-800 overflow-hidden transition-colors hover:border-slate-700">
    <div className="flex flex-row">
      <div className="relative w-32 h-24 bg-slate-900/20 border-r border-slate-700 flex-shrink-0 overflow-hidden">
        <ProductNewRibbon newUntil={product.newUntil} size="sm" />
        <img
          src={product.image}
          alt={product.name}
          className="w-full h-full object-contain"
          loading="lazy"
        />
      </div>

      <div className="flex-1 p-4">
        <div className="flex flex-row justify-between items-start">
          <div className="flex-1 mr-4">
            <div className="flex items-center mb-2">
              <span className="inline-flex items-center bg-slate-800 border border-slate-700 text-slate-200 text-xs font-medium px-2 py-1 rounded-none mr-2">
                {product.category}
              </span>
              {product.popular && (
                <span className="inline-flex items-center bg-purple-600 text-white text-xs font-medium px-2 py-1 rounded-none">
                  Populär
                </span>
              )}
            </div>

            <h3 className="text-lg font-bold text-white mb-2 line-clamp-1">{product.name}</h3>
            <p className="text-slate-400 text-sm line-clamp-2 leading-relaxed mb-3">
              {product.shortDescription}
            </p>

            <div className="flex items-center justify-between">
              <div>
                {product.priceOnRequest ? (
                  <>
                    <div className="text-xl font-bold text-purple-300">Enligt offert</div>
                    <div className="text-xs text-slate-400">Begär offert</div>
                  </>
                ) : (
                  <>
                    <PriceWithVatLabel
                      price={product.pricePerDay}
                      valueClassName="text-xl font-bold text-white"
                      labelClassName="text-xs text-slate-400"
                    />
                    <div className="text-xs text-slate-400">per dag</div>
                  </>
                )}
              </div>

              <div className="flex flex-row gap-2">
                <a
                  href={`/booking/${product.id}`}
                  onClick={() =>
                    trackBookingCtaClick(product, undefined, {
                      entry_point: 'grid',
                      list_name: 'LjusTieredProductGrid',
                    })
                  }
                  className="inline-flex justify-center items-center bg-purple-600 hover:bg-purple-700 text-white font-medium py-2 px-4 rounded-none transition-colors text-sm"
                >
                  Boka nu
                </a>
                <a
                  href={`/produkter/${product.slug || product.id}`}
                  className="inline-flex justify-center items-center bg-slate-900/20 hover:bg-slate-900/30 text-white font-medium py-2 px-4 rounded-none transition-colors duration-300 border border-slate-700 text-sm"
                >
                  Detaljer
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

const LjusTieredProductGrid: React.FC<Props> = ({ products, viewMode }) => {
  const groups = groupLjusProducts(products);
  void LJUS_TIER_STICKY_NAV;

  if (groups.length === 0) {
    return (
      <div className="text-center py-10">
        <p className="text-lg text-slate-400">
          Inga produkter hittades. Försök med en annan sökning eller kategori.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-12" data-testid="ljus-tiered-product-grid">
      {groups.map((group) => (
        <section
          key={group.tier}
          id={`ljus-tier-${group.tier}`}
          className={`relative border rounded-none p-5 md:p-8 ${tierSectionClass(group.tier)}`}
          aria-labelledby={`ljus-tier-heading-${group.tier}`}
        >
          <div className={`absolute left-0 top-0 bottom-0 w-1 ${tierAccentBar(group.tier)}`} />

          <header className="mb-6 md:mb-8 pl-3 md:pl-4">
            <p className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-2">
              Ljus · {group.title}
            </p>
            <h2
              id={`ljus-tier-heading-${group.tier}`}
              className={`text-3xl md:text-4xl font-bold tracking-tight ${tierTitleClass(group.tier)}`}
            >
              {group.title}
            </h2>
            <p className="mt-3 max-w-2xl text-base md:text-lg text-slate-300 leading-relaxed">
              {group.blurb}
            </p>
            {group.tier === 'scen_effektljus' ? (
              <p className="mt-3 inline-flex items-center border border-slate-600 bg-slate-900/60 px-3 py-1.5 text-xs font-medium uppercase tracking-wide text-slate-200">
                Scen / produktion
              </p>
            ) : null}
          </header>

          <div className="md:hidden grid grid-cols-1 gap-4">
            {group.products.map((product) => (
              <ProductCard key={`${group.tier}-${product.id}`} product={product} />
            ))}
          </div>

          {viewMode === 'grid' ? (
            <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {group.products.map((product) => (
                <ProductCard key={`${group.tier}-${product.id}`} product={product} />
              ))}
            </div>
          ) : (
            <div className="hidden md:block space-y-4">
              {group.products.map((product) => (
                <ProductListRow key={`${group.tier}-${product.id}`} product={product} />
              ))}
            </div>
          )}
        </section>
      ))}
    </div>
  );
};

export default LjusTieredProductGrid;
